<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { AlertCircle } from '@lucide/vue'

interface IProps {
  label: string
  placeholder?: string
  type?: string
  error?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<IProps>(), {
  label: 'Some label',
  placeholder: '',
  type: 'text',
})

const model = defineModel()

const isDismissed = ref(false)

watch(model, () => {
  if (props.error) {
    isDismissed.value = true
  }
})

watch(
  () => props.error,
  (newError) => {
    if (newError) {
      isDismissed.value = false
    }
  },
)

const hasError = computed(() => !!props.error && !isDismissed.value)
</script>

<template>
  <div class="space-y-1">
    <label
      v-if="label"
      class="text-sm font-bold text-foreground mb-1.5 inline-block dark:text-white"
      >{{ label }}</label
    >
    <input
      :type="type"
      v-model="model"
      :placeholder="placeholder"
      :disabled="disabled"
      class="w-full h-9 px-3 rounded-lg border bg-background text-sm text-foreground focus:outline-none focus:ring-1 transition-all dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
      :class="[
        hasError
          ? 'border-destructive/50 focus:ring-destructive/20'
          : 'border-input focus:ring-primary/50',
        disabled ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : '',
      ]"
    />
    <p v-if="hasError" class="text-xs text-destructive flex items-center gap-1.5">
      <AlertCircle :size="11" /> {{ props.error }}
    </p>
  </div>
</template>
